import type {Editor} from 'grapesjs';
import {isTextEntryElement} from '@Utils/dom.util';

/** Checked by node name because canvas-iframe elements fail `instanceof` against this document's constructors. */
const KEY_OWNING_NODE_NAMES = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON']);

const ESCAPE_KEY = 'Escape';
const ENTER_KEY = 'Enter';
const KEY_LISTENER_OPTIONS: AddEventListenerOptions = {capture: true};

export interface ImageCropKeyActions {
  onApply: () => void;
  onCancel: () => void;
}

export function attachImageCropKeyboard(editor: Editor, actions: ImageCropKeyActions): () => void {
  const canvasDocument = editor.Canvas.getDocument();
  const onKeyDown = (event: KeyboardEvent): void => {
    handleImageCropKeyDown(event, actions);
  };

  document.addEventListener('keydown', onKeyDown, KEY_LISTENER_OPTIONS);
  canvasDocument?.addEventListener('keydown', onKeyDown, KEY_LISTENER_OPTIONS);

  return (): void => {
    document.removeEventListener('keydown', onKeyDown, KEY_LISTENER_OPTIONS);
    canvasDocument?.removeEventListener('keydown', onKeyDown, KEY_LISTENER_OPTIONS);
  };
}

function handleImageCropKeyDown(event: KeyboardEvent, actions: ImageCropKeyActions): void {
  if (isKeyEventOwnedByTarget(event)) {
    return;
  }

  if (event.key === ESCAPE_KEY) {
    claimKeyEvent(event);
    actions.onCancel();
    return;
  }

  if (event.key === ENTER_KEY) {
    claimKeyEvent(event);
    actions.onApply();
  }
}

function claimKeyEvent(event: KeyboardEvent): void {
  event.preventDefault();
  event.stopPropagation();
}

function isKeyEventOwnedByTarget(event: KeyboardEvent): boolean {
  const {target} = event;
  if (!(target instanceof Node)) {
    return false;
  }
  if (isTextEntryElement(target) || KEY_OWNING_NODE_NAMES.has(target.nodeName)) {
    return true;
  }
  return 'isContentEditable' in target && target.isContentEditable === true;
}
